import { create } from 'zustand';

import { OfficeChatApi } from '@/api';

export interface IOfficeChatMessage {
  _id: string;
  agencyId: string;
  createdBy: string;
  createdByName: string;
  createdByInitials: string;
  message: string;
  createdAt: string;
  updatedAt?: string;
  isEdited: boolean;
  isPinned?: boolean;
  pinnedBy?: string;
  pinnedAt?: string;
  isSystemMessage?: boolean;
}

interface OfficeChatState {
  messages: IOfficeChatMessage[];
  pinnedMessage: IOfficeChatMessage | null;
  unreadCount: number;
  isPanelOpen: boolean;
  isLoading: boolean;
  isPolling: boolean;
  lastMessageId: string | null;
  currentUserId: string | null;
  hasAccess: boolean;
  isChatAdmin: boolean;

  // Actions
  setHasAccess: (hasAccess: boolean, isChatAdmin: boolean) => void;
  setCurrentUserId: (userId: string | null) => void;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setMessages: (messages: IOfficeChatMessage[]) => void;
  setPinnedMessage: (message: IOfficeChatMessage | null) => void;
  addMessage: (message: IOfficeChatMessage) => void;
  addMessages: (messages: IOfficeChatMessage[]) => void;
  updateMessage: (messageId: string, message: IOfficeChatMessage) => void;
  removeMessage: (messageId: string) => void;
  removeMessages: (messageIds: string[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsPolling: (isPolling: boolean) => void;
  setLastMessageId: (id: string | null) => void;
  markAsViewed: () => Promise<void>;
  loadUnreadCount: () => Promise<void>;
  resetUnread: () => void;
  reset: () => void;
}

const initialState = {
  messages: [] as IOfficeChatMessage[],
  pinnedMessage: null as IOfficeChatMessage | null,
  unreadCount: 0,
  isPanelOpen: false,
  isLoading: false,
  isPolling: false,
  lastMessageId: null as string | null,
  currentUserId: null as string | null,
  hasAccess: false,
  isChatAdmin: false,
};

export const useOfficeChatStore = create<OfficeChatState>((set, get) => ({
  ...initialState,

  setHasAccess: (hasAccess: boolean, isChatAdmin: boolean) =>
    set({ hasAccess, isChatAdmin }),

  setCurrentUserId: (userId: string | null) => {
    const { currentUserId: prevUserId } = get();

    if (prevUserId !== userId) {
      set({
        currentUserId: userId,
        messages: [],
        lastMessageId: null,
        unreadCount: 0,
      });
    }
  },

  openPanel: () => set({ isPanelOpen: true }),

  closePanel: () => set({ isPanelOpen: false }),

  togglePanel: () => {
    const { isPanelOpen } = get();
    set({ isPanelOpen: !isPanelOpen });
  },

  setPinnedMessage: (message: IOfficeChatMessage | null) =>
    set({ pinnedMessage: message }),

  setMessages: (messages: IOfficeChatMessage[]) => {
    const lastMessageId =
      messages.length > 0 ? messages[messages.length - 1]._id : null;
    set({ messages, lastMessageId });
  },

  addMessage: (message: IOfficeChatMessage) => {
    set((state) => ({
      messages: [...state.messages, message],
      lastMessageId: message._id,
    }));
  },

  addMessages: (messages: IOfficeChatMessage[]) => {
    if (messages.length === 0) return;

    const { messages: existingMessages, currentUserId } = get();

    const existingIds = new Set(existingMessages.map((m) => m._id));
    const newMessages = messages.filter((m) => !existingIds.has(m._id));

    if (newMessages.length === 0) return;

    const newLastMessageId = newMessages[newMessages.length - 1]._id;
    const countableNew = newMessages.filter(
      (m) => !m.isSystemMessage && m.createdBy !== currentUserId,
    );

    set((state) => ({
      messages: [...state.messages, ...newMessages],
      lastMessageId: newLastMessageId,
      unreadCount: state.unreadCount + countableNew.length,
    }));
  },

  updateMessage: (messageId: string, updatedMessage: IOfficeChatMessage) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg._id === messageId ? updatedMessage : msg,
      ),
    }));
  },

  removeMessage: (messageId: string) => {
    set((state) => ({
      messages: state.messages.filter((msg) => msg._id !== messageId),
      pinnedMessage:
        state.pinnedMessage?._id === messageId ? null : state.pinnedMessage,
    }));
  },

  removeMessages: (messageIds: string[]) => {
    if (messageIds.length === 0) return;
    const idSet = new Set(messageIds);
    set((state) => ({
      messages: state.messages.filter((msg) => !idSet.has(msg._id)),
      pinnedMessage:
        state.pinnedMessage && idSet.has(state.pinnedMessage._id)
          ? null
          : state.pinnedMessage,
    }));
  },

  setIsLoading: (isLoading: boolean) => set({ isLoading }),

  setIsPolling: (isPolling: boolean) => set({ isPolling }),

  setLastMessageId: (id: string | null) => set({ lastMessageId: id }),

  markAsViewed: async () => {
    try {
      await OfficeChatApi.markAsViewed();
      set({ unreadCount: 0 });
    } catch {
      // Caller may surface a toast; store stays silent.
    }
  },

  loadUnreadCount: async () => {
    try {
      const res = await OfficeChatApi.getUnreadCount();
      set({ unreadCount: res.data.count });
    } catch {
      // Leave the existing value on failure.
    }
  },

  resetUnread: () => set({ unreadCount: 0 }),

  reset: () => set(initialState),
}));
